<template>
  <div class="flex flex-col align-center h-full px-10">
    <h1 class="sm:text-3xl lg:text-4xl font-bold title-font text-default mb-4">
      Nouveau projet
    </h1>
    <!-- ANCHOR ERROR HANDLING -->
    <!-- ANCHOR FORM CREATE SNIPPET -->
    <form
      id="createSnippet"
      @submit.prevent="checkValidation"
      class="flex flex-wrap justify-evenly h-full gap-x-12 gap-y-4"
    >
      <div class="flex-1 shrink">
        <div class="p-field text-left">
          <label for="titre">Nom du projet</label>
          <InputText
            class="max-w-lg"
            id="title"
            maxlength="50"
            v-model.trim="project.title"
            required="true"
            autofocus :class="{'p-invalid': errors.title}"
          />
          <small v-if="errors.title" class="p-invalid text-red">{{ errors.title }}</small>
        </div>
        <div class="p-field">
          <label for="description">Description du projet (facultatif)</label>
          <Textarea
            class="max-w-lg"
            id="title"
            maxlength="255"
            v-model="project.description"
            rows="5"
            :class="{'p-invalid': errors.description}"
          />
          <small v-if="errors.description" class="p-invalid text-red">{{
              errors.description
            }}</small>
        </div>
        <div class="mb-5 mt-5">
          <h2 class="mb-3">Type de projet</h2>
          <SelectButton class="p-selectbutton-rounded mb-2" v-model="project.type"
                        :options="projectTypes" option-label="name" option-value="value"/>
          <div class="font-light max-w-lg">
            Le mode éducatif permet d’ajouter des élèves et de les répartir en groupes pour qu'ils
            puissent travailler sur un code modèle.
          </div>
        </div>
        <div v-if="project.type === 'teach'">
          <h2 class="mb-1">Mode examen</h2>
          <div class="font-light max-w-lg">
            En définissant une date de début et de fin, le code de votre projet
            ne sera éditable que dans la plage horaire définie.
          </div>
          <div class="p-field-checkbox flex flex-wrap align-center gap-1 mt-2">
            <div class="flex gap-2 mb-2">
              <Checkbox id="access-period" v-model="project.hasAccessPeriod" :binary="true"/>
              <label for="access-period">Activer le mode examen</label>
            </div>
            <div
              class="grid w-full gap-2"
              v-if="project.hasAccessPeriod"
            >
              <label for="startDate">Date de début : </label>
              <Calendar
                class="max-w-lg"
                v-model="project.startDate"
                :min-date="new Date()"
                :numberOfMonths="2"
                dateFormat="dd/mm/yy"
                show-time
                show-icon
              />
            </div>
            <div
              class="grid w-full gap-2"
              v-if="project.hasAccessPeriod"
            >
              <label for="endDate">Date de fin : </label>
              <Calendar
                class="max-w-lg"
                v-model="project.endDate"
                :min-date="minEndDate"
                :numberOfMonths="2"
                dateFormat="dd/mm/yy"
                show-time
                show-icon
              />
            </div>
          </div>
          <small v-if="errors.period" class="p-invalid text-red">{{ errors.period }}</small>
        </div>
      </div>
      <div class="">
        <div class="mb-8">
          <h2 class="mb-1">Membres du projet</h2>
          <div class="font-light max-w-lg">Ajoutez ici les adresses mail des participants.
            S’ils n’ont pas encore de compte sur CodeSnippet, nous leur enverrons une invitation pour
            rejoindre votre projet !
          </div>
          <Chips id="description" class="p-chips-multiline mt-5"
                 v-model="project.participantsEmail" :allowDuplicate="false"
                 placeholder="membre@gmail.com" separator=","
                 addOnBlur />
        </div>
        <div>
          <h2 class="mb-1">Administrateurs du projet</h2>
          <Chips id="description" class="p-chips-multiline mt-5"
                 v-model="project.adminsEmail" :allowDuplicate="false"
                 placeholder="admin@gmail.com" separator=","
                 addOnBlur />
        </div>
      </div>
      <div class="block w-full mt-auto">
        <Button
          class="btn-green m-auto"
          type="submit"
          :is-loading="isProjectSaving"
          label="Lancer le projet"
        />
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import { mapActions } from "vuex"
import moment from "moment"

export default {
  name: 'CreateSnippet',
  data: () => ({
    errors: {
      title: null,
      description: null,
      period: null
    },
    projectTypes: [
      { name: 'Individuel / Équipe', value: "team" },
      { name: 'Enseignement', value: "teach" }
    ],
    project: {
      title: '',
      description: '',
      type: 'team',
      hasAccessPeriod: false,
      startDate: null,
      endDate: null,
      participantsEmail: [],
      adminsEmail: [],
    },
    isProjectSaving: false,
  }),
  computed: {
    minEndDate() {
      // Used to keep user from choosing a end date before the start date
      return moment(this.project.startDate).toDate()
    }
  },
  watch: {
    "project.startDate": function(newStartDate) {
      // If the new start date is later than the endDate, endDate is reset.
      if(this.project.endDate && moment(newStartDate).isAfter(this.project.endDate)) {
        this.project.endDate = null
      }
    },
  },
  methods: {
    ...mapActions({
      fetchRecents: "projects/fetchRecents",
    }),
    checkValidation() {
      if (
        this.project.type === 'teach'
        && (this.project.hasAccessPeriod && (!this.project.startDate || !this.project.endDate))
      ) {
        this.$toast.add({
          severity: 'error',
          summary: 'Formulaire incomplet',
          detail: `Les dates d'examen sont obligatoires`,
          life: 3000
        })
        this.errors.period = 'Les dates d\'examen sont obligatoires'
      } else {
        // Reset errors before creating project
        this.errors = {
          title: null,
          description: null,
          period: null
        }
        this.createProject()
      }
    },
    formatErrorValidation(errors) {
      for (const [key, value] of Object.entries(errors)) {
        if (!this.errors[key]) {
          this.errors[key] = value[0]
        }
      }
    },
    async createProject() {
      try {
        this.isProjectSaving = true
        const newProjectResponse = await axios.post("/projects",
          {
            title: this.project.title,
            description: this.project.description,
            type: this.project.hasAccessPeriod ? 'examen' : this.project.type,
            start_date: this.project.hasAccessPeriod
              ? moment(this.project.startDate).format('YYYY-MM-DD H:m:s')
              : null,
            end_date: this.project.hasAccessPeriod
              ? moment(this.project.endDate).format('YYYY-MM-DD H:m:s')
              : null,
            members: this.project.participantsEmail,
            admin_members: this.project.adminsEmail,
          },
          {
            'headers': {
              Authorization: `Bearer ${this.$store.state.auth.token}`
            }
          }
        )

        this.$toast.add({
          severity: 'success',
          summary: 'Projet créé',
          detail: 'Le projet a été créé avec succès',
          life: 3000
        })

        this.fetchRecents()
        await this.$router.push({
          name: 'Edit Project',
          params: { idProject: newProjectResponse.data.id, idGroup: newProjectResponse.data.group_id }
        })
      } catch (error) {
        this.formatErrorValidation(error.response.data.errors)
      }
      finally {
        this.isProjectSaving = false
      }
    },
  },
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
.label-choice .card-ui {
  min-height: 250px;
}

#step1, #step2 {
  transition-property: left, right;
  transition-duration: 1s, 1s;
  right: 0%;
  width: 100%
}

#step2 {
  position: absolute;
  top: 0;
  width: 100%;
  background: var(--bg-lighter);
}

.currentStep {
  position: absolute;
  right: 0%;
}

#step1.hiddenStep {
  position: absolute;
  right: 100%;
}

#step2.hiddenStep {
  position: absolute;
  right: -100%;
}

input[type="radio"] + label span, input[type="checkbox"] + label span {
  transition: all .2s,
  transform .2s;
}

input[type="radio"] + label span:hover,
input[type="radio"] + label:hover span,
input[type="checkbox"] + label span:hover,
input[type="checkbox"] + label:hover span {
  transform: scale(1.1);
}

input[type="radio"]:checked + label span, input[type="checkbox"]:checked + label span {
  background-color: var(--primary); //bg-blue
  box-shadow: 0px 0px 0px 5px white inset;
}

input[type="radio"]:checked + label, input[type="checkbox"]:checked + label {
  color: #3490DC; //text-blue
}
</style>
